import React, { useEffect, useState, useRef, useCallback } from 'react';
import useInputKeys from 'hooks/useInputKeys';
import ActiveBare from 'assets/images/active-bare-bg.png';
import DisabledBare from 'assets/images/disabled-bare-bg.png';
import EnabledBare from 'assets/images/enabled-bare-bg.png';
import EnabledBareCyan from 'assets/images/enabled-Bare-cyan.png';
import EnabledBareGreen from 'assets/images/enabled-Bare-green.png';
import EnabledBarePink from 'assets/images/enabled-Bare-pink.png';
import EnabledBareLightCyan from 'assets/images/enabled-Bare-lightCyan.png';
import EnabledBareLightGreen from 'assets/images/enabled-Bare-lightGreen.png';
import './index.scss';
import { useTheme } from '@emotion/react';
import { useThemeContext } from 'theme/ThemeContextProvider';

function NumberKeyboard({ setInput }) {
    const {
        currentTimeline,
        currentBare,
        play,
        setPlay,
        setCurrentTimeMusic,
        manualCurrentTimeMusic,
        setManualCurrentTimeMusic,
        trackFetchingLoading,
        setTrackFetchingLoading,
        keepPlay,
        setKeepPlay,
    } = useInputKeys();
    const [font, setFont] = useState(0);
    const [currentKeyboardRangeStart, setCurrentKeyboardRangeStart] = useState(0);
    const [backgroundNeedToRerender, setBackgroundNeedToRerender] = useState(false);
    const [innerCurrentBare, setInnerCurrentBare] = useState(currentBare);
    const { mode } = useThemeContext();
    const theme = useTheme();

    const audioContextRef = useRef(null);
    const audioBufferRef = useRef(null);
    const sourceNodeRef = useRef(null);
    const startTimeRef = useRef(0);
    const pauseTimeRef = useRef(0);
    const isPlayingRef = useRef(false);
    const svgRef = useRef(null);

    const [isDragging, setIsDragging] = useState(false);
    const isTouchingRef = useRef(false);
    const draggingBareNumberRef = useRef(-1);
    const [currentDraggRadius, setCurrentDraggRadius] = useState(380);
    const [loopStartBare, setLoopStartBare] = useState(-2);
    const [loopEndBare, setLoopEndBare] = useState(-2);
    const draggStartPos = useRef({ x: 0, y: 0 });
    const dragDirectinRef = useRef(null);
    const [bareLength, setBareLength] = useState(0);

    const getImageEnableHref = () => {
        return mode === 'light' ? EnabledBare
            : mode === 'Cyan' ? EnabledBareCyan
                : mode === 'Green' ? EnabledBareGreen
                    : mode === 'Pink' ? EnabledBarePink
                        : mode === 'LightCyan' ? EnabledBareLightCyan
                            : mode === 'LightGreen' ? EnabledBareLightGreen
                                : '';
    };

    const getImageActiveHref = () => {
        return mode === 'Green' ? EnabledBareCyan
            : mode === 'LightGreen' ? EnabledBareCyan
                : ActiveBare;
    };

    const seekAudio = (time) => {
        if (typeof time !== 'number' || isNaN(time)) {
            console.warn('Invalid time provided to seekAudio');
            return;
        }
        if (!audioBufferRef.current) {
            console.warn('Audio buffer not loaded yet');
            return;
        }

        if (sourceNodeRef.current) {
            try {
                sourceNodeRef.current.onended = null;
                sourceNodeRef.current.stop();
            } catch (error) {
                console.error('Error stopping previous source node:', error);
            }
            sourceNodeRef.current = null;
        }
    
        if (audioContextRef.current) {
            const sourceNode = audioContextRef.current.createBufferSource();
            sourceNode.buffer = audioBufferRef.current;
            sourceNode.loop = false;
            sourceNode.connect(audioContextRef.current.destination);

            startTimeRef.current = audioContextRef.current.currentTime - time;
            pauseTimeRef.current = time;

            sourceNode.start(0, time);
            sourceNodeRef.current = sourceNode;
            isPlayingRef.current = true;
    
            sourceNode.onended = () => {
                isPlayingRef.current = false;
                sourceNodeRef.current = null;
            };
        } else {
            console.warn('AudioContext not available for seeking');
        }
    };

    const handleTimeUpdate = useCallback(() => {
        if (audioContextRef.current) {
            let currentTime = audioContextRef.current.currentTime - startTimeRef.current;
            const endTime = currentTimeline?.timestamps[loopEndBare+1];
            const startTime = currentTimeline?.timestamps[loopStartBare];
            if(endTime && endTime < currentTime){
                currentTime = startTime;
                startTimeRef.current = audioContextRef.current.currentTime - startTime;
            }
            
            const i = currentTimeline?.timestamps?.findIndex(t => t > currentTime + 0.0001);
            setInput(i);
            setInnerCurrentBare(i);
            setCurrentTimeMusic(currentTime);
        }
    }, [setInput, currentTimeline, setCurrentTimeMusic, loopEndBare, loopStartBare]);

    const playAudio = useCallback(() => {
        if (!audioBufferRef.current || !audioContextRef.current) {
            return;
        }

        if (sourceNodeRef.current) {
            sourceNodeRef.current.onended = null;
            sourceNodeRef.current.stop();
            sourceNodeRef.current = null;
        }

        if (audioBufferRef.current && audioContextRef.current) {
            const sourceNode = audioContextRef.current.createBufferSource();
            sourceNode.buffer = audioBufferRef.current;
            sourceNode.loop = false;
            sourceNode.connect(audioContextRef.current.destination);

            const offset = pauseTimeRef.current;
            startTimeRef.current = audioContextRef.current.currentTime - offset;
            sourceNode.start(0, offset);
            sourceNodeRef.current = sourceNode;
            isPlayingRef.current = true;

            sourceNode.onended = () => {
                isPlayingRef.current = false;
                sourceNodeRef.current = null;
            };
        }
    }, []);

    const pauseAudio = useCallback(() => {
        if (sourceNodeRef.current) {
            try {
                sourceNodeRef.current.onended = null;
                sourceNodeRef.current.stop();
                if (audioContextRef.current) {
                    pauseTimeRef.current = audioContextRef.current.currentTime - startTimeRef.current;
                } else {
                    pauseTimeRef.current = 0;
                }
            } catch (error) {
                console.error('Error stopping source node during pause:', error);
            }
            isPlayingRef.current = false;
            sourceNodeRef.current = null;
        }
    }, []);

    const loopBaare = useCallback((startTime, endTime, currentTime) => {

        if (sourceNodeRef.current) {
            try {
                sourceNodeRef.current.onended = null;
                sourceNodeRef.current.stop();
            } catch (error) {
                console.error('Error stopping previous source node:', error);
            }
            sourceNodeRef.current = null;
        }

        if (audioBufferRef.current && audioContextRef.current) {
            const sourceNode = audioContextRef.current.createBufferSource();
            sourceNode.buffer = audioBufferRef.current;
            sourceNode.loop = true;
            sourceNode.loopStart = startTime;
            sourceNode.loopEnd = endTime;
            sourceNode.connect(audioContextRef.current.destination);

            startTimeRef.current = audioContextRef.current.currentTime - currentTime;
            pauseTimeRef.current = currentTime;
            sourceNode.start(0, currentTime);
            sourceNodeRef.current = sourceNode;
            isPlayingRef.current = true;

        } else {
            console.warn('AudioBuffer or AudioContext not available for looping');
        }
    }, []);

    const handleBareChange = useCallback((i) => {
        if (currentTimeline?.timestamps && audioBufferRef.current ) {
            const startTime = currentTimeline.timestamps[i] || 0;
            startTimeRef.current = audioContextRef.current.currentTime - startTime;
            pauseTimeRef.current = startTime;
            if(play) {
                seekAudio(startTime);
            } else {
                handleTimeUpdate();
            }
            
        }
    }, [currentTimeline, play, handleTimeUpdate]);

    const resetComponent = useCallback(() => {
        setPlay(false);
        setLoopStartBare(-2);
        setLoopEndBare(-2);
        pauseTimeRef.current = 0;
        startTimeRef.current = 0;
        isPlayingRef.current = false;
        audioBufferRef.current = null;
        if (sourceNodeRef.current) {
            try {
                sourceNodeRef.current.onended = null;
                sourceNodeRef.current.stop();
            } catch (error) {
                console.error('Error stopping previous source node:', error);
            }
            sourceNodeRef.current = null;
        }
        if (!audioContextRef.current) {
            audioContextRef.current = new (window.AudioContext || window.webkitAudioContext)();
        }
        return () => {
            if (sourceNodeRef.current) {
                sourceNodeRef.current.onended = null;
                sourceNodeRef.current.stop();
            }
            if (audioContextRef.current) {
                audioContextRef.current.close();
            }
        };
    }, [setPlay]);

    const onDraggEnd = useCallback((event) => {
        event.preventDefault();
        if(!isTouchingRef.current) return false;
        isTouchingRef.current=false;
        if(draggingBareNumberRef.current >= bareLength) return;
        if(dragDirectinRef.current === 'forward') {
            const fromStart = Math.abs(loopStartBare - draggingBareNumberRef.current)
            const fromEnd = Math.abs(loopEndBare - draggingBareNumberRef.current)

            if(Math.min(fromEnd,fromStart)>1){
                setLoopStartBare(draggingBareNumberRef.current)
                setLoopEndBare(draggingBareNumberRef.current)
            }
            else{
                if(draggingBareNumberRef.current>loopEndBare) {
                    setLoopEndBare(draggingBareNumberRef.current)
                }
                else{
                    setLoopStartBare(draggingBareNumberRef.current)
                }
            }
        }
        else if(dragDirectinRef.current === 'back'){
            if(draggingBareNumberRef.current === loopEndBare && draggingBareNumberRef.current === loopStartBare){
                setLoopStartBare(-2);
                setLoopEndBare(-2);
                if(draggingBareNumberRef.current+1 !== currentBare) {
                    handleBareChange(draggingBareNumberRef.current);
                }
            }
            else if(draggingBareNumberRef.current >= loopStartBare && draggingBareNumberRef.current < loopEndBare){
                setLoopStartBare(draggingBareNumberRef.current+1);
                handleBareChange(draggingBareNumberRef.current+1);
            } else {
                setLoopStartBare(-2);
                setLoopEndBare(-2);
            }
        }
        else {
            if(draggingBareNumberRef.current > loopEndBare || draggingBareNumberRef.current < loopStartBare) {
                setLoopStartBare(-2)
                setLoopEndBare(-2)
            }
            handleBareChange(draggingBareNumberRef.current)
        }
        dragDirectinRef.current = null;
    }, [bareLength, currentBare, handleBareChange, loopEndBare,loopStartBare]);

    const onDragging = useCallback((event) => {
        event.preventDefault();
        if (!isTouchingRef.current) return;
        if(svgRef.current) {
            let target = event;
            if(event.touches?.length) {
                target = event.touches[0];
            }
            const rect = svgRef.current.getBoundingClientRect();
            const cx = rect.left + rect.width / 2
            const cy = rect.top + rect.height / 2
            const radius = ((cx - target.clientX)**2 + (cy - target.clientY)**2)**0.5
            const oldRadius = ((cx - draggStartPos.current.x)**2 + (cy - draggStartPos.current.y)**2)**0.5
            const deltaRadius = oldRadius - radius;
            const newDraggRadius =  Math.max(350,Math.min(380,380-deltaRadius*2));
           if(deltaRadius !== 0) {
                dragDirectinRef.current = deltaRadius > 0 ? 'forward' : 'back';
                event.touches?.length ? onDraggEnd(event) : setCurrentDraggRadius(newDraggRadius);
           }
           
        }
    }, [onDraggEnd]);

    const onDragStart = useCallback((event) => {
        event.preventDefault();
        if(trackFetchingLoading) return;
        let target = event;
        if(event.touches?.length){
            target = event.touches[0];
        }
        const rect = svgRef.current.getBoundingClientRect();
        const cx = rect.left + rect.width / 2;
        const cy = rect.top + rect.height / 2;
        let angleRadians = Math.atan2(target.clientY - cy, target.clientX - cx) + (Math.PI / 2) + ((5 * Math.PI) / (6 * 33));
        if (angleRadians < 0) {
            angleRadians = 2 * Math.PI + angleRadians;
        }
        const selectedBare = Math.floor((angleRadians / (2 * Math.PI)) * 33) + currentKeyboardRangeStart;
        isTouchingRef.current = true;
        draggingBareNumberRef.current = selectedBare;
        draggStartPos.current = { x: target.clientX, y: target.clientY };
    }, [currentKeyboardRangeStart, trackFetchingLoading]);

    useEffect(()=>{
        if(currentTimeline?.timestamps?.length >0 ) {
            setBareLength(currentTimeline?.timestamps?.length)
        }
    },[currentTimeline?.timestamps]);

    useEffect(() => {
        setPlay(isPlayingRef.current);
    }, [setPlay]);

    const handleFetchingTrack = useCallback(async () => {
        if (!currentTimeline?.singleTrackUrl) return;

        try {
            setTrackFetchingLoading(true);
            const response = await fetch(currentTimeline.singleTrackUrl);
            const arrayBuffer = await response.arrayBuffer();
            const audioBuffer = await audioContextRef.current.decodeAudioData(arrayBuffer);
            audioBufferRef.current = audioBuffer;
            setPlay(true);
        } catch (error) {
            console.error('Error loading audio buffer:', error);
        } finally {
            setTrackFetchingLoading(false);
        }

    }, [currentTimeline.singleTrackUrl, setPlay, setTrackFetchingLoading]);

    useEffect(() => {
        if((play && !audioBufferRef.current) || keepPlay) {
            handleFetchingTrack();
            setKeepPlay(false);
        }
    }, [
        currentTimeline?.singleTrackUrl,
        handleFetchingTrack,
        play,
        keepPlay,
        setKeepPlay,
    ]);

    useEffect(() => {
        resetComponent();
    }, [
        currentTimeline?.singleTrackUrl,
        resetComponent,
    ]);

    useEffect(() => {
        let animationFrameId;
        const updateCurrentTime = () => {
            if (isPlayingRef.current && audioContextRef.current) {
                handleTimeUpdate();
            }
            animationFrameId = requestAnimationFrame(updateCurrentTime);
        };

        updateCurrentTime();

        return () => cancelAnimationFrame(animationFrameId);
    }, [handleTimeUpdate, currentTimeline?.singleTrackUrl]);

    useEffect(() => {
        if (play && audioBufferRef.current && audioContextRef.current) {
            playAudio();
        } else {
            pauseAudio();
        }
        /* eslint-disable react-hooks/exhaustive-deps */
    }, [play, playAudio, pauseAudio, audioBufferRef.current]);

    useEffect(() => {
        if (
            manualCurrentTimeMusic !== null &&
            manualCurrentTimeMusic !== undefined &&
            typeof manualCurrentTimeMusic === 'number' &&
            !isNaN(manualCurrentTimeMusic) &&
            audioBufferRef.current &&
            audioContextRef.current
        ) {
            pauseTimeRef.current = audioContextRef.current.currentTime - manualCurrentTimeMusic;
            startTimeRef.current = manualCurrentTimeMusic;

            seekAudio(manualCurrentTimeMusic);
            setManualCurrentTimeMusic(null);
        }
    }, [
        manualCurrentTimeMusic,
        setManualCurrentTimeMusic,
        audioBufferRef.current,
        audioContextRef.current
    ]);

    useEffect(() => {
        return () => {
            if (sourceNodeRef.current) {
                sourceNodeRef.current.onended = null;
                sourceNodeRef.current.stop();
            }
            if (audioContextRef.current) {
                audioContextRef.current.close();
            }
        };
    }, []);

    useEffect(() => {
        if (innerCurrentBare !== currentBare)
            handleBareChange(currentBare - 1)
        let validRangeMinimum = parseInt((currentBare - 1) / 33) * 33;
        if (validRangeMinimum !== currentKeyboardRangeStart)
            setCurrentKeyboardRangeStart(validRangeMinimum)
    }, [currentBare, innerCurrentBare, currentKeyboardRangeStart, handleBareChange])

    useEffect(()=>{
        if(loopStartBare < 0 || loopEndBare < 0) return;
        
        let currentTime = audioContextRef.current.currentTime - startTimeRef.current;
        const loopStart = currentTimeline?.timestamps[loopStartBare];
        const loopEnd = currentTimeline?.timestamps[loopEndBare+1];

        if(currentTime < loopStart || currentTime > loopEnd) {
            currentTime = loopStart;
        }

        loopBaare(loopStart,loopEnd, currentTime);
    },[loopBaare, loopStartBare,loopEndBare, currentTimeline, currentTimeline.timestamps]);

    useEffect(() => {
        svgRef.current = document.getElementById('svgWrapper');
        if (svgRef.current) {
            svgRef.current.setAttribute(
                "style","touch-action:none;"
            )
            const centerX = 481;
            const centerY = 481;
            let radius = 380;
            const numPaths = (parseInt(bareLength / 33) + 1) * 33;
            const angleIncrement = (2 * Math.PI) / 33;
            for (let i = 0; i < numPaths; i++) {
                if((isDragging) && i===draggingBareNumberRef.current && dragDirectinRef.current)
                    radius = currentDraggRadius
                else if(loopStartBare <= i && loopEndBare>= i)
                    radius = 350
                else
                    radius = 380
                const angle = i * angleIncrement - Math.PI / 2 + ((5 * Math.PI) / (6 * 33));
                const x = centerX + radius * Math.cos(angle);
                const y = centerY + radius * Math.sin(angle);
                const tangentAngle = angle + Math.PI / 2;
                let lastGroup = document.getElementById(`group_${i}`);
                if (currentKeyboardRangeStart >= 0 && lastGroup)
                    svgRef.current?.removeChild(lastGroup);


                const group = document.createElementNS("http://www.w3.org/2000/svg", "g");
                const path = document.createElementNS("http://www.w3.org/2000/svg", "path");
                group.setAttribute('id', `group_${i}`);
                path.setAttribute('id', `path_${i}`);
                path.setAttribute('d', 'm68.6 102.2c-10-0.8-20.2-1.2-30.3-1.2-9.8 0-19.8 0.4-29.6 1.2l-7.7-99.8c12.3-0.9 24.9-1.4 37.3-1.4 12.7 0 25.6 0.5 38.3 1.5z');
                path.setAttribute('transform', `translate(${x - 69}, ${y - 101.21}) rotate(${tangentAngle * 180 / Math.PI - 5}, 69, 101.21)`);
                path.setAttribute('stroke-width', '0');
                path.setAttribute('style', 'overflow: hidden');
                group.appendChild(path);

                if (i === 0) {
                    setTimeout(() => {
                        const pathBoundingRect = path.getBoundingClientRect();
                        setFont(1.4 * Math.round(pathBoundingRect.width))
                    });
                }
                const text = document.createElementNS("http://www.w3.org/2000/svg", "text");
                text.setAttribute('x', x - 30);
                text.setAttribute('y', y - 50);
                text.setAttribute('font-size', `${font}px`);
                text.setAttribute('font-family', 'bahnschrift');
                text.setAttribute('text-anchor', 'middle');
                text.setAttribute('dominant-baseline', 'central');
                text.setAttribute('transform', `rotate(${(tangentAngle * 180) / Math.PI - 5}, ${x}, ${y})`);
                group.setAttribute('style', 'cursor: pointer; font-family: bahnschrift');
                text.textContent = `0${(i + 1)}`.slice(-2);

                if (i < bareLength - 1) {
                    if (i < currentBare - 1) {
                        text.setAttribute('fill', theme.palette.bareColors.main);
                    }
                    if (i === currentBare - 1) {
                        text.setAttribute('fill', '#000000');
                    }
                    if (i > currentBare - 1) {
                        text.setAttribute('fill', '#535353');
                    }
                }

                if (i >= bareLength - 1) {
                    text.setAttribute('fill', '#808080');
                }
                group.appendChild(text);
                if (i < currentKeyboardRangeStart || i >= currentKeyboardRangeStart + 33) {
                    group.setAttribute("style", 'display:none;')

                }
                svgRef.current.appendChild(group);
            }

            svgRef.current.addEventListener('mousedown', onDragStart);
            svgRef.current.addEventListener('touchstart', onDragStart);
            svgRef.current.addEventListener('mousemove', onDragging);
            svgRef.current.addEventListener('touchmove', onDragging);
            svgRef.current.addEventListener('mouseleave', onDraggEnd);
            svgRef.current.addEventListener('mouseup', onDraggEnd);
            svgRef.current.addEventListener('touchend',onDraggEnd);
            svgRef.current.addEventListener('touchcancel',onDraggEnd);
            
        }
        setBackgroundNeedToRerender(backgroundNeedToRerender => !backgroundNeedToRerender);

        return () => {
            if (svgRef.current) {
                svgRef.current.removeEventListener('mousedown', onDragStart);
                svgRef.current.removeEventListener('touchstart', onDragStart);
                svgRef.current.removeEventListener('mousemove', onDragging);
                svgRef.current.removeEventListener('touchmove', onDragging);
                svgRef.current.removeEventListener('mouseup', onDraggEnd);
                svgRef.current.removeEventListener('mouseleave', onDraggEnd);
                svgRef.current.removeEventListener('touchend', onDraggEnd);
                svgRef.current.removeEventListener('touchcancel', onDraggEnd);
            }
            svgRef.current = null;
        };

    }, [
        font,
        currentTimeline,
        currentTimeline?.singleTrackUrl,
        handleBareChange,
        currentKeyboardRangeStart,
        currentBare,
        theme,
        mode,
        isDragging,
        setIsDragging,
        onDraggEnd,
        currentDraggRadius,
        draggStartPos,
        bareLength, 
        loopEndBare,
        loopStartBare,
        onDragStart,
        onDragging,
        currentTimeline?.singleTrackUrl
    ]);

    useEffect(() => {
        const bareLength = currentTimeline?.timestamps?.length || 0;
        const numPaths = (parseInt(bareLength / 33) + 1) * 33
        for (let i = 0; i < numPaths; i++) {
            const path = document.getElementById(`path_${i}`);
            const group = document.getElementById(`group_${i}`);
            if (path && group) {
                path.setAttribute('fill', 'url(#disabledMode)');
                if (i < currentBare) {
                    path.setAttribute('fill', 'url(#enabledMode)');
                }
                if (i + 1 === currentBare && play) {
                    path.setAttribute('fill', 'url(#activeMode)');
                }
            }
        }

    }, [currentTimeline, currentTimeline?.singleTrackUrl, currentBare, setInput, play, handleTimeUpdate, currentKeyboardRangeStart, backgroundNeedToRerender, theme, mode]);

    return (
        <svg id="svgWrapper" width="962" height="962" viewBox="0 0 962 962" fill="none" xmlns="http://www.w3.org/2000/svg">
            <defs>
                <pattern id="activeMode" patternUnits="userSpaceOnUse" width="110" height="99">
                    <image href={getImageActiveHref()} x="0" y="0" width="78" height="104" />
                </pattern>
                <pattern id="enabledMode" patternUnits="userSpaceOnUse" width="110" height="99">
                    <image href={getImageEnableHref()} x="0" y="0" width="78" height="104" />
                </pattern>
                <pattern id="disabledMode" patternUnits="userSpaceOnUse" width="110" height="99" style={{ backgroundColor: 'red' }}>
                    <image href={DisabledBare} x="0" y="0" width="78" height="104" />
                </pattern>
            </defs>
        </svg>
    );
}

export default NumberKeyboard;
